import React, { useMemo } from 'react';
import { AnimatedValue } from './AnimatedValue';
import './ReportsNative.css';

/**
 * Horizontal ranked bar list (per revenue-overview.html bottom row and
 * the "Expenses by Vendor" / "Overtime Wages by Property" cards):
 * name · bar · mono value. The leader gets the full orange gradient +
 * emphasized label/amount; the rest use a lighter orange ramp.
 *
 * Props:
 *   items        [{ id?, label, value, display?, onClick? }]
 *   format       value format key or fn (default 'currencyCompact')
 *   currency     ISO code for currency formats
 *   sorted       items are already ordered (default false → sorts desc)
 *   max          bar scale max (default: largest item value)
 *   labelWidth   first column width px (default 118)
 *   amountWidth  last column width px (default 64)
 *   limit        cap number of rows shown
 */
export function RankedBars({
  selectedId,
  items = [],
  format = 'currencyCompact',
  currency,
  sorted = false,
  max,
  labelWidth = 118,
  amountWidth = 64,
  limit,
  className,
}) {
  const rows = useMemo(() => {
    const safe = items.filter((it) => it && Number.isFinite(Number(it.value)));
    const ordered = sorted ? safe : [...safe].sort((a, b) => Number(b.value) - Number(a.value));
    return limit ? ordered.slice(0, limit) : ordered;
  }, [items, sorted, limit]);

  const scaleMax = max ?? rows.reduce((m, it) => Math.max(m, Number(it.value)), 0);

  if (rows.length === 0) {
    return <div className="rn-empty">No data</div>;
  }

  return (
    <div className={`rn-rank-list${className ? ` ${className}` : ''}`}>
      {rows.map((item, i) => {
        const pct = scaleMax > 0 ? Math.max(2, (Number(item.value) / scaleMax) * 100) : 0;
        const top = i === 0;
        const clickable = typeof item.onClick === 'function';
        return (
          <div
            key={item.id ?? item.label ?? i}
            className={`rn-rank-line${top ? ' rn-rank-top' : ''}${clickable ? ' rn-rank-clickable' : ''}${selectedId != null ? (item.id === selectedId ? ' rn-rank-selected' : ' rn-rank-dim') : ''}`}
            style={{ gridTemplateColumns: `${labelWidth}px 1fr ${amountWidth}px` }}
            onClick={item.onClick}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={clickable ? (e) => (e.key === 'Enter' || e.key === ' ') && item.onClick(item) : undefined}
          >
            <span className="rn-rank-name" title={item.label}>
              {item.label}
            </span>
            <span className="rn-rank-bar">
              <i style={{ width: `${pct}%` }} />
            </span>
            <span className="rn-rank-amt">
              {item.display ?? <AnimatedValue value={item.value} format={format} currency={currency} />}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default RankedBars;
